// @flow

import React from 'react';

import { Link } from '@zapier/design-system';

import errorBlock from 'app/get-help/components/blocks/Error';
import help from 'app/get-help/components/blocks/Help';
import { createDefaultContact } from 'app/get-help/content/nodes/loggedIn/zapTroubleshooting/common';
import { ZENDESK_CUSTOM_FIELD_IDS } from 'app/get-help/constants';


const defaultContact = createDefaultContact();

const error = errorBlock({
  label: 'What is the error you ran into?',
  zendeskCustomFieldId: ZENDESK_CUSTOM_FIELD_IDS.ERROR_WHAT_IS_THE_ERROR,
  customChild: help({
    contact: defaultContact,
  }),
  items: [
    {
      value: 'four',
      label: 'Error code 400-499',
      zendeskCustomFieldValue: 'cf-error-400-499',
      child: help({
        docs: {
          title: 'Identify and fix the error',
          body: () => (
            <p>
              Errors in the 400-499 range often mean that there was a problem
              with what the Zap was asking the app to do. For tips on what your
              error means and how to troubleshoot this range of errors,{' '}
              <Link href="/help/troubleshoot/errors/troubleshoot-error-codes-in-zaps">
                check out our guide here
              </Link>
              .
            </p>
          ),
        },
        // TODO: Enable once we have enough resources for community moderation
        // community: [
        //   {
        //     title:
        //       'Things NOT to do on Google Sheets that are involved in live zaps',
        //     href:
        //       'https://community.zapier.com/discussion/195/things-not-to-do-on-google-sheets-that-are-involved-in-live-zaps',
        //     snippet:
        //       'A set of tips from a Zapier Expert to avoid breaking Zaps while editing a Google Sheet, and how to get the most out of Sheets on Zapier',
        //   },
        //   {
        //     title: 'Custom Values Not Working',
        //     href:
        //       'https://community.zapier.com/discussion/486/custom-value-not-working',
        //     snippet:
        //       'A Zap creator learns how to use custom values to change the item being updated each time their Zap runs.',
        //   },
        // ],
        contact: defaultContact,
      }),
    },
    {
      value: 'five',
      label: 'Error code 500-599, timeout, or other server issue',
      zendeskCustomFieldValue: 'cf-error-500-599',
      child: help({
        docs: {
          title: 'Identify and fix the error',
          body: () => (
            <p>
              Errors in the 500-599 range or that mention timeouts or server
              issues often mean that there was an issue with the app at the time
              the Zap tried to do something. For tips on what your error means
              and how to troubleshoot this range of errors,{' '}
              <Link href="/help/troubleshoot/errors/troubleshoot-error-codes-in-zaps">
                check out our guide here
              </Link>
              .
            </p>
          ),
        },
        // TODO: Enable once we have enough resources for community moderation
        // community: [
        //   {
        //     title: 'Daily Limit Exceeded',
        //     href: 'https://community.zapier.com/discussion/comment/1849',
        //     snippet:
        //       'Some apps have limits on the amount of data passed to other apps. This conversation explains some ways to avoid hitting those limits.',
        //   },
        //   {
        //     title: 'Google drive to youtube error',
        //     href: 'https://community.zapier.com/discussion/comment/1206',
        //     snippet:
        //       'An explanation of why a timeout occurred, and how to avoid timeouts in the future.',
        //   },
        // ],
        contact: defaultContact,
      }),
    },
    {
      value: 'required',
      label: '"Required field is missing" or "Please enter a value"',
      zendeskCustomFieldValue: 'cf-field-missing-enter-value',
      child: help({
        docs: {
          title: 'Check if the field is blank',
          body: () => (
            <p>
              An error with this sort of message usually means that there was a
              value missing for a field. This might have happened if that field
              was blank. You can often fix this by making sure that field is{' '}
              <Link href="/help/create/basics/set-up-your-zap-action#customize-your-options">
                mapped in your action step
              </Link>
              , or making sure that data existed when the Zap was running by{' '}
              <Link href="/app/history">checking your Zap History</Link>.
            </p>
          ),
        },
        // TODO: Enable once we have enough resources for community moderation
        // community: [
        //   {
        //     title: `Cannot read property 'number' of undefined`,
        //     href: 'https://community.zapier.com/discussion/comment/1840',
        //     snippet:
        //       'Advice on how to identify which data maps to which fields across triggers and actions, and how to format the data accordingly.',
        //   },
        //   {
        //     title: 'How to map Google calendar reminders?',
        //     href: 'https://community.zapier.com/discussion/comment/1966',
        //     snippet:
        //       'A Community member shares a workaround for a trigger that doesn’t exist.',
        //   },
        //   {
        //     title: 'Custom Values Not Working',
        //     href:
        //       'https://community.zapier.com/discussion/486/custom-value-not-working',
        //     snippet:
        //       'A Zap creator learns how to use custom values to change the item being updated each time their Zap runs.',
        //   },
        // ],
        contact: defaultContact,
      }),
    },
    {
      value: 'notfound',
      label: '"Record was not found"',
      zendeskCustomFieldValue: 'cf-record-not-found',
      child: help({
        docs: {
          title: 'Check your search step or lookup fields',
          body: () => (
            <p>
              An error with this sort of message could happen if your search
              step failed, or if your action step has a field that looks up a
              linked record. Ensure that if you are using{' '}
              <Link href="/help/create/customize/add-custom-values-to-dropdown-menu-fields-in-zaps">
                custom values
              </Link>
              , the custom value passed to your action is one that exists
              already in your action. You can see what Zapier searched for by
              looking in your <Link href="/app/history">Zap History</Link>, and
              making changes to formatting or mapped fields if needed.
            </p>
          ),
        },
        // TODO: Enable once we have enough resources for community moderation
        // community: [
        //   {
        //     title: `Custom Values Not Working`,
        //     href:
        //       'https://community.zapier.com/discussion/486/custom-value-not-working',
        //     snippet:
        //       'A Zap creator learns how to use custom values to change the item being updated each time their Zap runs.',
        //   },
        //   {
        //     title:
        //       'Need some help with a Jira Zap - creating a Jira "issue" type, from google sheets',
        //     href:
        //       'https://community.zapier.com/discussion/425/need-some-help-with-a-jira-zap-creating-a-jira-issue-type-from-google-sheets',
        //     snippet:
        //       'A user’s data isn’t passing from Google Sheets into Jira, so a community member suggests a way to double check the data is formatted correctly.',
        //   },
        // ],
        contact: defaultContact,
      }),
    },
    {
      value: 'other',
      label: 'Other error',
      zendeskCustomFieldValue: 'cf-other-error',
      child: help({
        docs: {
          title: 'Identify and fix the error',
          body: () => (
            <p>
              Errors can happen for many reasons—the way the Zap is set up, a
              bug, or even permissions problems. For information on
              troubleshooting common error types,{' '}
              <Link href="/help/troubleshoot/errors/troubleshoot-error-codes-in-zaps">
                check out this guide.
              </Link>
            </p>
          ),
        },
        // TODO: Enable once we have enough resources for community moderation
        // community: [
        //   {
        //     title: `Daily Limit Exceeded`,
        //     href:
        //       'https://community.zapier.com/discussion/comment/1849#Comment_1849',
        //     snippet:
        //       'Some apps have limits on the amount of data passed to other apps. This conversation explains some ways to avoid hitting those limits.',
        //   },
        //   {
        //     title: 'Zap not triggering - Facebook leads to Mailchimp',
        //     href:
        //       'https://community.zapier.com/discussion/452/zap-not-triggering-facebook-leads-to-mailchimp',
        //     snippet:
        //       'Some apps have permissions or settings in their own software that cause complications in Zapier; in this discussion, a Zapier Expert guides a Zap creator through correcting this.',
        //   },
        //   {
        //     title:
        //       'Things NOT to do on Google Sheets that are involved in live zaps',
        //     href:
        //       'https://community.zapier.com/discussion/195/things-not-to-do-on-google-sheets-that-are-involved-in-live-zaps#latest',
        //     snippet:
        //       'A set of tips from a Zapier Expert to avoid breaking Zaps while editing in Google Sheets, and to get the most out of Sheets on Zapier.',
        //   },
        // ],
        contact: defaultContact,
      }),
    },
  ],
});

export default error;
